import {
  bestEstimatedOneRepMax,
  deriveCardioTrend,
  deriveExerciseTrend,
  deriveRepMaxTable,
  strengthVolume,
  type CardioSegment,
  type CardioTrendPoint,
  type DerivedRecord,
  type ExerciseTrendPoint,
  type LoggedSet,
  type PerformedExercise,
  type RepMaxRow,
} from '@liftx/core';
import { db, type LiftXDatabase } from '../db';
import { isLive } from '../tombstone';
import type { CardioSegmentRecord, PersonalRecordRecord, SessionRecord } from '../types';

/**
 * Dexie row -> the shape `@liftx/core`'s cardio functions expect. The exact
 * same small adapter `sessionRepository.ts` and `CardioPanel.tsx` each keep
 * their own copy of (C2/C4) — matching that established per-consumer
 * convention rather than exporting a shared one across an unrelated module
 * boundary for a six-line pure function.
 */
function toCardioSegment(row: CardioSegmentRecord): CardioSegment {
  return {
    id: row.id,
    sessionExerciseId: row.sessionExerciseId,
    segmentIndex: row.orderIndex,
    durationS: row.durationS,
    distanceM: row.distanceM,
    calories: null,
    avgHr: row.avgHeartRate,
    maxHr: null,
    inclinePct: row.inclinePct ?? null,
    resistanceLevel: null,
    isCompleted: row.isCompleted,
  };
}

/**
 * Aggregation for Progress: exercise trends, PR summaries, and Dashboard
 * reads. Master Phase 8.
 *
 * Kept separate from `historyRepository.ts` (pure session browsing, no
 * `sessionSets` join) and `personalRecordsRepository.ts` (record-cache
 * persistence only). Personal-record reads go through the existing cache
 * (`personalRecordsRepository.listCurrent`) — this file only reshapes it for
 * presentation. `deriveExerciseTrend` has no cached equivalent, so
 * `getExerciseTrend` genuinely folds over raw session history every call,
 * memoised here so repeat views of the same exercise in one session don't
 * repeat the join.
 */

export interface WeeklyVolume {
  volumeGramReps: number;
  isPartial: boolean;
}

export interface ExercisePrSummary {
  repMaxTable: RepMaxRow[];
  bestEstimatedOneRepMaxG: number | null;
  /** Every non-`rep_max` record kind currently held. */
  otherRecords: PersonalRecordRecord[];
}

export interface ProgressRepository {
  getExerciseTrend(exerciseId: string): Promise<ExerciseTrendPoint[]>;
  /**
   * Cardio Phase C7. A deliberate sibling to `getExerciseTrend`, not a
   * modality branch inside it — `ExerciseTrendPoint` and `CardioTrendPoint`
   * are different shapes for a reason (see `progress.ts`'s own comment).
   * The caller — `ExerciseTrendDetailScreen` — knows the exercise's modality
   * already and picks which of the two to call, the same way `CardioPanel`
   * vs. `ExercisePanel` is chosen in Active Workout (C4).
   */
  getCardioTrend(exerciseId: string): Promise<CardioTrendPoint[]>;
  /** Every exercise with at least one current (non-superseded) record, most-recently-achieved first. */
  listExercisesWithRecords(): Promise<{ exerciseId: string; mostRecentAchievedAt: number }[]>;
  getExercisePrSummary(exerciseId: string): Promise<ExercisePrSummary>;
  getWeeklyVolumeGramReps(weekStartLocalDate: string): Promise<WeeklyVolume>;
  listRecentPersonalRecords(limit: number): Promise<PersonalRecordRecord[]>;
  listRecentSessions(limit: number): Promise<SessionRecord[]>;
  /** Drops the in-memory trend caches (both strength and cardio) — called after any write that could change a trend's shape. */
  invalidate(exerciseId?: string): void;
}

/** The exclusive end of a 7-day window starting at `weekStartLocalDate` ('YYYY-MM-DD'). */
function weekEnd(weekStartLocalDate: string): string {
  const start = new Date(`${weekStartLocalDate}T00:00:00Z`);
  const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  return end.toISOString().slice(0, 10);
}

export function createProgressRepository(database: LiftXDatabase = db): ProgressRepository {
  /**
   * A plain per-exercise cache, not a Web Worker offload (Decision AN of the
   * approved Phase 8 plan): this is a single-user app doing a pure-JS fold
   * with no CPU-heavy step, so a worker's complexity was judged
   * disproportionate to the realistic history size involved. Invalidated
   * wholesale by any write path that already triggers
   * `personalRecordsRepository.rebuildForExercise` — over-invalidating costs
   * nothing real at this scale.
   */
  const trendCache = new Map<string, ExerciseTrendPoint[]>();
  /** Cardio Phase C7. Kept separate from `trendCache` above rather than one
   *  map of a union type — the two point shapes are never mixed for the
   *  same exercise in practice (a real exercise is one modality), and a
   *  separate map means `getCardioTrend` cannot accidentally read or write
   *  a strength-shaped cache entry. */
  const cardioTrendCache = new Map<string, CardioTrendPoint[]>();

  async function loadHistory(exerciseId: string): Promise<PerformedExercise[]> {
    const sessionExercises = await database.sessionExercises.where('exerciseId').equals(exerciseId).filter(isLive).toArray();

    const history: PerformedExercise[] = [];
    for (const exercise of sessionExercises) {
      const session = await database.sessions.get(exercise.sessionId);
      if (session === undefined || !isLive(session)) continue;

      const sets = (await database.sessionSets
        .where('sessionExerciseId')
        .equals(exercise.id)
        .filter(isLive)
        .toArray()) as LoggedSet[];

      history.push({
        sessionExerciseId: exercise.id,
        sessionId: exercise.sessionId,
        exerciseId,
        modality: exercise.modality,
        sessionStartedAt: session.startedAt,
        sets,
      });
    }
    return history;
  }

  /**
   * Cardio Phase C7. The exact structural mirror of `loadHistory` above,
   * reading `cardioSegments` instead of `sessionSets`. A pre-C3 ("legacy")
   * cardio session has no live `cardioSegments` rows at all — the query
   * below correctly returns `[]` for it, not an error, so it folds to a
   * genuine zero point via `deriveCardioTrend` rather than being dropped or
   * crashing. Its historical `sessionSets` are untouched by this function
   * and by everything else in this file — nothing here reads or rewrites
   * them.
   */
  async function loadCardioHistory(exerciseId: string): Promise<PerformedExercise[]> {
    const sessionExercises = await database.sessionExercises.where('exerciseId').equals(exerciseId).filter(isLive).toArray();

    const history: PerformedExercise[] = [];
    for (const exercise of sessionExercises) {
      const session = await database.sessions.get(exercise.sessionId);
      if (session === undefined || !isLive(session)) continue;

      const segments = (
        await database.cardioSegments.where('sessionExerciseId').equals(exercise.id).filter(isLive).toArray()
      ).map(toCardioSegment);

      history.push({
        sessionExerciseId: exercise.id,
        sessionId: exercise.sessionId,
        exerciseId,
        modality: exercise.modality,
        sessionStartedAt: session.startedAt,
        sets: [],
        segments,
      });
    }
    return history;
  }

  const repository: ProgressRepository = {
    async getExerciseTrend(exerciseId): Promise<ExerciseTrendPoint[]> {
      const cached = trendCache.get(exerciseId);
      if (cached !== undefined) return cached;

      const history = await loadHistory(exerciseId);
      const trend = deriveExerciseTrend(history);
      trendCache.set(exerciseId, trend);
      return trend;
    },

    async getCardioTrend(exerciseId): Promise<CardioTrendPoint[]> {
      const cached = cardioTrendCache.get(exerciseId);
      if (cached !== undefined) return cached;

      const history = await loadCardioHistory(exerciseId);
      const trend = deriveCardioTrend(history);
      cardioTrendCache.set(exerciseId, trend);
      return trend;
    },

    async listExercisesWithRecords(): Promise<{ exerciseId: string; mostRecentAchievedAt: number }[]> {
      const rows = await database.personalRecords.filter((row) => row.supersededAt === null).toArray();
      const byExercise = new Map<string, number>();
      for (const row of rows) {
        const current = byExercise.get(row.exerciseId) ?? 0;
        if (row.achievedAt > current) byExercise.set(row.exerciseId, row.achievedAt);
      }
      return [...byExercise.entries()]
        .map(([exerciseId, mostRecentAchievedAt]) => ({ exerciseId, mostRecentAchievedAt }))
        .sort((a, b) => b.mostRecentAchievedAt - a.mostRecentAchievedAt);
    },

    async getExercisePrSummary(exerciseId): Promise<ExercisePrSummary> {
      const rows = await database.personalRecords
        .where('exerciseId')
        .equals(exerciseId)
        .filter((row) => row.supersededAt === null)
        .toArray();

      const asDerived: DerivedRecord[] = rows.map((row) => ({
        kind: row.kind,
        repCount: row.repCount,
        value: row.value,
        unit: row.unit,
        achievedAt: row.achievedAt,
        sessionId: row.sessionId ?? '',
        setId: row.setId,
      }));

      return {
        repMaxTable: deriveRepMaxTable(asDerived),
        bestEstimatedOneRepMaxG: bestEstimatedOneRepMax(asDerived),
        otherRecords: rows.filter((row) => row.kind !== 'rep_max'),
      };
    },

    async getWeeklyVolumeGramReps(weekStartLocalDate): Promise<WeeklyVolume> {
      const sessions = await database.sessions
        .where('localDate')
        .between(weekStartLocalDate, weekEnd(weekStartLocalDate), true, false)
        .filter((session) => isLive(session) && (session.status === 'completed' || session.status === 'abandoned'))
        .toArray();

      let volumeGramReps = 0;
      let isPartial = false;
      for (const session of sessions) {
        const exercises = await database.sessionExercises.where('sessionId').equals(session.id).filter(isLive).toArray();
        for (const exercise of exercises) {
          const sets = (await database.sessionSets
            .where('sessionExerciseId')
            .equals(exercise.id)
            .filter(isLive)
            .toArray()) as LoggedSet[];
          const volume = strengthVolume(sets);
          volumeGramReps += volume.volumeGramReps;
          if (volume.isPartial) isPartial = true;
        }
      }
      return { volumeGramReps, isPartial };
    },

    async listRecentPersonalRecords(limit): Promise<PersonalRecordRecord[]> {
      const rows = await database.personalRecords.filter((row) => row.supersededAt === null).toArray();
      return rows.sort((a, b) => b.achievedAt - a.achievedAt).slice(0, limit);
    },

    async listRecentSessions(limit): Promise<SessionRecord[]> {
      const rows = await database.sessions
        .filter((session) => isLive(session) && (session.status === 'completed' || session.status === 'abandoned'))
        .toArray();
      return rows.sort((a, b) => b.startedAt - a.startedAt).slice(0, limit);
    },

    invalidate(exerciseId): void {
      if (exerciseId === undefined) {
        trendCache.clear();
        cardioTrendCache.clear();
      } else {
        trendCache.delete(exerciseId);
        cardioTrendCache.delete(exerciseId);
      }
    },
  };

  return repository;
}

export const progressRepository = createProgressRepository();
